// Coordinates are stored in model space so camera movement and tilting do not
// alter measurements. A reference supplies metres per model unit.
export const UNITS = { m: 1, cm: 0.01, mm: 0.001, ft: 0.3048, in: 0.0254 };
export const distance = (a, b) => Math.hypot(...a.map((v, i) => v - b[i]));

export function polygonInfo(points) {
  if (points.length < 3) return { area: 0, valid: false };
  const normal = [0, 0, 0];
  points.forEach((p, i) => {
    const q = points[(i + 1) % points.length];
    normal[0] += (p[1] - q[1]) * (p[2] + q[2]);
    normal[1] += (p[2] - q[2]) * (p[0] + q[0]);
    normal[2] += (p[0] - q[0]) * (p[1] + q[1]);
  });
  const magnitude = Math.hypot(...normal);
  const span = Math.max(...points.map(p => distance(p, points[0])));
  if (!span || magnitude < span * span * 1e-8) return { area: 0, valid: false };
  const n = normal.map(v => v / magnitude);
  const drop = normal.map(Math.abs).indexOf(Math.max(...normal.map(Math.abs)));
  const flat = points.map(p => p.filter((_, i) => i !== drop));
  const cross = (a, b, c) => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  const epsilon = span * span * 1e-10;
  // Reject crossing or overlapping non-adjacent edges rather than reporting a
  // misleading signed area for a bow-tie polygon.
  const onSegment = (a, b, p) => Math.abs(cross(a, b, p)) <= epsilon &&
    p.every((v, i) => v >= Math.min(a[i], b[i]) - span * 1e-10 && v <= Math.max(a[i], b[i]) + span * 1e-10);
  for (let i = 0; i < flat.length; i += 1) {
    const a = flat[i], b = flat[(i + 1) % flat.length];
    if (distance(points[i], points[(i + 1) % points.length]) < span * 1e-8) return { area: 0, valid: false };
    for (let j = i + 2; j < flat.length; j += 1) {
      if (i === 0 && j === flat.length - 1) continue;
      const c = flat[j], d = flat[(j + 1) % flat.length];
      if ((cross(a, b, c) * cross(a, b, d) < 0 && cross(c, d, a) * cross(c, d, b) < 0) ||
          onSegment(a, b, c) || onSegment(a, b, d) || onSegment(c, d, a) || onSegment(c, d, b)) {
        return { area: 0, valid: false };
      }
    }
  }
  const maxDeviation = Math.max(...points.map(p => Math.abs(p.reduce((sum, v, i) => sum + (v - points[0][i]) * n[i], 0))));
  return { area: magnitude / 2, valid: true, nonPlanar: maxDeviation > span * 0.02, normal: n };
}

export function measurementValue(measurement, scale, unit) {
  const factor = scale / UNITS[unit];
  return measurement.kind === 'area'
    ? polygonInfo(measurement.points).area * factor * factor
    : distance(...measurement.points) * factor;
}

export function validSavedState(value) {
  const validPoints = (points, min, max) => Array.isArray(points) && points.length >= min && points.length <= max &&
    points.every(p => Array.isArray(p) && p.length === 3 && p.every(Number.isFinite));
  return value && UNITS[value.unit] && Array.isArray(value.measurements) && value.measurements.length <= 200 &&
    value.measurements.every(m => ['distance', 'area'].includes(m.kind) && validPoints(m.points, m.kind === 'area' ? 3 : 2, m.kind === 'area' ? 100 : 2)) &&
    (!value.reference || (validPoints(value.reference.points, 2, 2) && distance(...value.reference.points) > 0 && Number.isFinite(value.reference.metres) && value.reference.metres > 0));
}
